import BaseInput, { BaseTextFieldProps, SelectOption, selectOptionLabelToPlainString } from './base-input.component';
import MaterialUIPickers, { MaterialUIPickersProps, PickerType } from './picker.component';
import CountrySelect from './country-select.component';
import SelectInputMultiple, { Option, SelectInputMultipleProps } from './select-input-multiple';
import { ForwardedRef, forwardRef, type ElementType } from 'react';
import SelectAutocompleteComponent, { SelectInputAutocompleteAsyncProps } from './select-input-autocomplete-async';
import i18n from '../../i18n';
import GoogleMapComponent, { GooglePlace } from '../../page/my-restaurant/map/google-search';
import FlagInput from '../../page/my-restaurant/flag-input.component';
import PrefixLabeledPillSelect, { PrefixLabeledPillSelectOption } from '../prefix-labeled-pill-select.component';
import PrefixLabeledPillMultiSelect, { PrefixLabeledPillMultiSelectOption } from '../prefix-labeled-pill-multi-select.component';
// 表单通用组件
export default forwardRef(function CommonInput(props: {
    inputType?: CommonInputType;
    handleChangePicker?: MaterialUIPickersProps['handleChange'];
    onClosePicker?: MaterialUIPickersProps['onClosePicker'];
    handleChange?: (val: any) => void;
    handleChangeCountry?: (value: string) => void;
    rangeValue?: [Date | null, Date | null];
    minDate?: Date | null;
    maxDate?: Date | null;
    filterTime?(date: Date): boolean;
    multipleProps?: SelectInputMultipleProps;
    rangeHandleChangePicker?: (value?: [Date | null, Date | null], error?: boolean) => void;
    fetchData?: (label?: string) => Promise<SelectOption[]>;
    handleChangeGooglePlace?: (val: GooglePlace) => void;
    // 语言
    language?: string;
    labelPrefix?: string;
    leadingIcon?: React.ReactNode;
    /** SELECT_PILL_MUL 时可选，多选药丸下拉最大宽度（px） */
    pillMultiMaxWidth?: number;
    /** SELECT_PILL_MUL 时可选，占位符字号（如 20 或 '20px'） */
    pillMultiPlaceholderFontSize?: number | string;
    /** SELECT_PILL：下拉使用虚拟列表（大数据量，见 PrefixLabeledPillSelect.virtualizeMenu） */
    virtualizePillMenu?: boolean;
    /** SELECT_PILL：右侧下拉箭头图标组件（如 `tabler_chevron-down.svg` 的 ReactComponent） */
    pillDropdownIcon?: ElementType;
} & (Partial<BaseTextFieldProps>), ref: ForwardedRef<HTMLDivElement>) {
    // useTranslation();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {
        inputType = 'INPUT',
        allowClear,
        handleChange,
        onClosePicker,
        handleChangeGooglePlace,
        handleChangeCountry,
        minDate,
        maxDate,
        filterTime,
        multipleProps,
        pillMultiMaxWidth,
        pillMultiPlaceholderFontSize,
        virtualizePillMenu,
        pillDropdownIcon,
        ...otherProps
    } = props;
    // const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
    switch (inputType) {
        case 'INPUT': {
            return (
                <BaseInput
                    allowClear={allowClear}
                    ref={ref}
                    {...otherProps as BaseTextFieldProps}
                />
            )
        }
        case 'SELECT_MUL': {
            return (
                <SelectInputMultiple
                    value={otherProps.value as unknown as Option[]}
                    label={otherProps.label}
                    {...otherProps as SelectInputMultipleProps}
                    {...multipleProps as SelectInputMultipleProps}
                />
            )
        }
        // 选择输入框
        case 'SELECT': {
            return (
                <BaseInput
                    allowClear={allowClear}
                    select={true}
                    ref={ref}
                    {...otherProps as BaseTextFieldProps}
                />

            )
        }
        /** 单选下拉框-圆角药丸 */
        case 'SELECT_PILL': {
            return (
                <PrefixLabeledPillSelect
                    options={otherProps.items as unknown as PrefixLabeledPillSelectOption[]}
                    value={String(otherProps.value ?? '') as any}
                    onChange={(value) => otherProps.onChange?.({ target: { value } } as any)}
                    labelPrefix={otherProps.labelPrefix as string}
                    leadingIcon={otherProps.leadingIcon}
                    size={otherProps.size as 'small' | 'medium'}
                    sx={otherProps.sx}
                    virtualizeMenu={virtualizePillMenu}
                    dropdownIconComponent={pillDropdownIcon}
                    className={otherProps.className}
                />
            )
        }
        /** 多选下拉框-圆角药丸：选中项以 Chip 展示在框内，可点击 Chip 删除图标取消选中 */
        case 'SELECT_PILL_MUL': {
            const multiValue = Array.isArray(otherProps.value) ? (otherProps.value as string[]) : (otherProps.value != null ? [String(otherProps.value)] : []);
            const multiOptions = (otherProps.items || []).map((item) => ({ value: String(item.value), label: String(item.label ?? '') })) as PrefixLabeledPillMultiSelectOption[];
            return (
                <PrefixLabeledPillMultiSelect
                    options={multiOptions}
                    value={multiValue}
                    onChange={(nextValues) => otherProps.onChange?.({ target: { value: nextValues } } as any)}
                    labelPrefix={otherProps.labelPrefix as string}
                    leadingIcon={otherProps.leadingIcon}
                    placeholder={otherProps.placeholder as string}
                    placeholderFontSize={pillMultiPlaceholderFontSize}
                    size={otherProps.size as 'small' | 'medium'}
                    maxWidth={pillMultiMaxWidth}
                    sx={otherProps.sx}
                />
            )
        }
        // 国家区号选择器
        case 'COUNTRY_SELECT': {
            const value = props.value as string || '39';
            return (
                <CountrySelect
                    countryVal={value}
                    disabled={props.disabled ?? false}
                    onChange={handleChangeCountry}
                    className={otherProps.className}
                    label={otherProps.label}
                />
            )
        }
        // 自动完成选择器
        case 'AUTO_COMPLETE_SELECT': {
            return (
                <SelectAutocompleteComponent
                    value={props.value as SelectOption}
                    onChange={handleChange}
                    fetchData={props.fetchData}
                    {...otherProps as SelectInputAutocompleteAsyncProps}
                />
            )
        }

        // 时间 日期 选择器
        // case 'DATE': {
        //     const pickerType = props.inputType as PickerType;
        //     const value = props.value as unknown as Date;
        //     return (
        //         props.handleChangePicker ? <MaterialUIPickers
        //             pickerType={pickerType}
        //             value={value}
        //             onClosePicker={onClosePicker}
        //             handleChange={props.handleChangePicker}
        //             textFieldProps={{
        //                 inputProps: {
        //                     size: otherProps.size,
        //                 },
        //                 ...otherProps
        //             }}
        //         /> : <></>
        //     )
        // }
        /** 单时间（紧凑）：样式同 `TIME_RANGE_COMPACT` 单侧，无 label */
        case 'TIME_COMPACT': {
            const pickerType = props.inputType as PickerType;
            const value = props.value as unknown as Date;
            return (
                props.handleChangePicker ? <MaterialUIPickers
                    pickerType={pickerType}
                    value={value || null}
                    onClosePicker={onClosePicker}
                    handleChange={(v) => { props.handleChangePicker?.(v) }}
                    textFieldProps={{
                        ...otherProps,
                        label: undefined,
                        inputProps: {
                            ...((otherProps as BaseTextFieldProps).inputProps || {}),
                            size: otherProps.size,
                        },
                    }}
                    minDate={minDate}
                    maxDate={maxDate}
                    filterTime={filterTime}
                /> : <></>
            );
        }
        // 时间范围（紧凑）：无 label、边框交互在 picker 内处理，逻辑同 TIME_RANGE
        case 'TIME_RANGE_COMPACT': {
            const pickerType = props.inputType as PickerType;
            const value = props.value as unknown as Date;
            return (
                props.rangeHandleChangePicker ? <MaterialUIPickers
                    pickerType={pickerType}
                    value={value || null}
                    onClosePicker={onClosePicker}
                    handleChange={(v) => { props.handleChangePicker?.(v) }}
                    rangeHandleChangePicker={props.rangeHandleChangePicker}
                    textFieldProps={{
                        inputProps: {
                            size: otherProps.size,
                        },
                        ...otherProps,
                        label: undefined,
                    }}
                    rangeValue={props.rangeValue}
                    minDate={minDate}
                    maxDate={maxDate}
                    filterTime={filterTime}
                /> : <></>
            );
        }
        // 时间 日期 日期时间范围 选择器
        case 'DATE':
        case 'TIME':
        case 'DATE_TIME':
        case 'DATE_TIME_RANGE':
        case 'TIME_RANGE':
        case 'DATE_RANGE': {
            const pickerType = props.inputType as PickerType;
            const value = props.value as unknown as Date;
            return (
                props.handleChangePicker || props.rangeHandleChangePicker ? <MaterialUIPickers
                    pickerType={pickerType}
                    value={value || null}
                    onClosePicker={onClosePicker}
                    handleChange={(value) => { props.handleChangePicker?.(value) }}
                    rangeHandleChangePicker={props.rangeHandleChangePicker}
                    textFieldProps={{
                        inputProps: {
                            size: otherProps.size,
                        },
                        ...otherProps,
                        label: typeof otherProps.label !== 'string' ? otherProps.label : i18n.t(otherProps.label || '')
                    }}
                    rangeValue={props.rangeValue}
                    minDate={minDate}
                    maxDate={maxDate}
                    filterTime={filterTime}
                /> : <></>
            )
        }
        // 谷歌地图地点选择器
        case 'GOOGLE_PLACE': {
            return (
                <GoogleMapComponent
                    defaultValue={otherProps.value as string || ''}
                    onSave={(val) => {
                        handleChangeGooglePlace?.(val)
                    }}
                    onChange={props.onChange}
                    languageCode={props.language}
                    hiddenLabel={Boolean((otherProps as BaseTextFieldProps).hiddenLabel)}
                />
            )
        }
        // 国家和地区选择
        case 'FLAG_INPUT': {
            return (
                <FlagInput
                    countryList={(otherProps.items || []).map((item) => ({
                        value: String(item.value),
                        label: selectOptionLabelToPlainString(item.label),
                    }))}
                    countryValue={otherProps.value as string}
                    onChange={(value) => otherProps.onChange?.({ target: { value } } as React.ChangeEvent<HTMLInputElement>)}
                    required={otherProps.required}
                    disabled={otherProps.disabled}
                    hiddenLabel={Boolean((otherProps as BaseTextFieldProps).hiddenLabel)}
                />
            )
        }
        default: {
            return null;
        }
    }
})

export type CommonInputType = 'INPUT' | 'SELECT_MUL' | 'SELECT' | 'SEARCH' | 'COUNTRY_SELECT' | 'AUTO_COMPLETE_SELECT' | PickerType | 'GOOGLE_PLACE' | 'FLAG_INPUT' | 'SELECT_PILL' | 'SELECT_PILL_MUL'
